import { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, DatePicker, Card, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useStations } from '@/hooks/useStations';
import { fetchStrategyGuide, StrategyGuideResponse } from '@/api/optimization';
import { BASE_DATAZOOM, format2 } from '@/utils/charts';

/** 96-point time labels: "00:00", "00:15", "00:30", ... */
const timeLabels = Array.from({ length: 96 }, (_, i) =>
  `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`
);

export default function StrategyGuidePage() {
  const isMobile = useIsMobile();
  const { data: stationData } = useStations();
  const stations = stationData?.stations ?? [];

  const [station, setStation] = useState<string>(stations[0] ?? '');
  const [date, setDate] = useState(dayjs().add(1, 'day'));
  const [data, setData] = useState<StrategyGuideResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync station if stations list loads after initial render
  useEffect(() => {
    if (!station && stations.length > 0) {
      setStation(stations[0]);
    }
  }, [station, stations]);

  const doFetch = useCallback(async () => {
    if (!station || !date) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchStrategyGuide(station, date.format('YYYY-MM-DD'));
      setData(result);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setData(null);
        setError('该日期对应数据暂未生成');
      } else {
        setError(e?.response?.data?.detail ?? '加载失败');
      }
    } finally {
      setLoading(false);
    }
  }, [station, date]);

  useEffect(() => {
    if (station) {
      doFetch();
    }
  }, [station, date, doFetch]);

  // ── Chart 1: PV Forecast (Top-Left) ──
  const pvChart: EChartsOption | null = useMemo(() => {
    if (!data?.pv_forecast) return null;
    return {
      tooltip: { trigger: 'axis' },
      grid: { top: 10, left: 55, right: 20, bottom: 30 },
      xAxis: { data: timeLabels, axisLabel: { interval: 7 } },
      yAxis: { name: 'MW', type: 'value', nameTextStyle: { fontWeight: 'bold', fontSize: 12 } },
      dataZoom: BASE_DATAZOOM as any,
      series: [
        {
          name: '光伏预测',
          type: 'bar',
          data: data.pv_forecast,
          itemStyle: { color: '#f59e0b' },
          barWidth: 4,
        },
      ],
    };
  }, [data]);

  // ── Chart 2: Curtailment + AGC (Top-Right) ──
  const curtChart: EChartsOption | null = useMemo(() => {
    if (!data?.agc_target_pred && !data?.curtailment_rate) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['AGC目标预测', '限电率'], bottom: 0 },
      grid: { top: 10, left: 60, right: 60, bottom: 30 },
      xAxis: { data: timeLabels, axisLabel: { interval: 7 } },
      yAxis: [
        { name: 'MW', type: 'value', nameTextStyle: { fontWeight: 'bold', fontSize: 12 } },
        { name: '%', type: 'value', min: 0, max: 100, nameTextStyle: { fontWeight: 'bold', fontSize: 12 } },
      ],
      dataZoom: BASE_DATAZOOM as any,
      series: [
        ...(data.agc_target_pred
          ? [{
              name: 'AGC目标预测',
              type: 'bar' as const,
              data: data.agc_target_pred,
              yAxisIndex: 0,
              itemStyle: { color: '#93c5fd' },
              barWidth: 4,
            }]
          : []),
        ...(data.curtailment_rate
          ? [{
              name: '限电率',
              type: 'line' as const,
              data: data.curtailment_rate.map((v: number) => v * 100),
              yAxisIndex: 1,
              lineStyle: { color: '#ef4444' },
              itemStyle: { color: '#ef4444' },
              symbol: 'none',
              smooth: true,
            }]
          : []),
      ],
    };
  }, [data]);

  // ── Chart 3: EP + Price (Bottom-Left) ──
  const epChart: EChartsOption | null = useMemo(() => {
    if (!data?.optimal_ep && !data?.price_forecast) return null;
    const epData = data.optimal_ep ?? [];
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['推荐EP', '电价预测'], bottom: 0 },
      grid: { top: 10, left: 60, right: 60, bottom: 30 },
      xAxis: { data: timeLabels, axisLabel: { interval: 7 } },
      yAxis: [
        { name: 'MW', type: 'value', nameTextStyle: { fontWeight: 'bold', fontSize: 12 } },
        { name: '元/MWh', type: 'value', nameTextStyle: { fontWeight: 'bold', fontSize: 12 } },
      ],
      dataZoom: BASE_DATAZOOM as any,
      series: [
        {
          name: '推荐EP',
          type: 'bar',
          data: epData,
          yAxisIndex: 0,
          barWidth: 4,
          itemStyle: {
            color: (params: any) =>
              (params.value as number) < 0 ? '#3b82f6' : '#ef4444',
          },
        },
        ...(data.price_forecast
          ? [{
              name: '电价预测',
              type: 'line' as const,
              data: data.price_forecast,
              yAxisIndex: 1,
              lineStyle: { color: '#ef4444' },
              itemStyle: { color: '#ef4444' },
              symbol: 'none',
            }]
          : []),
      ],
    };
  }, [data]);

  // ── Quadrant 4: Expected Metrics (Bottom-Right) ──
  const metricsCard = useMemo(() => {
    if (!data) {
      return (
        <Card title="预期指标" style={{ height: '100%' }}>
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
            {error || '选择电站和日期后自动加载'}
          </div>
        </Card>
      );
    }

    const { expected, yesterday_compare } = data;

    const rows = [
      { label: '净收益', today: expected.net_revenue, yesterday: yesterday_compare?.net_revenue, suffix: '元', color: '#16a34a' },
      { label: '储能充电量', today: expected.bat_charge, yesterday: yesterday_compare?.bat_charge, suffix: 'MWh', color: '#3b82f6' },
      { label: '储能放电量', today: expected.bat_discharge, yesterday: yesterday_compare?.bat_discharge, suffix: 'MWh', color: '#ef4444' },
      { label: '弃电量', today: expected.curtailment, yesterday: yesterday_compare?.curtailment, suffix: 'MWh', color: '#f59e0b' },
    ];

    return (
      <Card title="预期指标" style={{ height: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
          {rows.map(row => {
            const delta = row.yesterday != null ? row.today - row.yesterday : null;
            const yestVal = row.yesterday ?? 0;
            const deltaPct = delta != null && yestVal !== 0
              ? (delta / Math.abs(yestVal)) * 100
              : null;

            return (
              <div key={row.label} style={{
                background: '#f9fafb',
                borderRadius: 8,
                padding: '12px 16px',
              }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{row.label}</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: row.color }}>
                  {format2(row.today)}{row.suffix}
                </div>
                {delta != null && (
                  <div style={{
                    fontSize: 12, color: delta >= 0 ? '#16a34a' : '#dc2626', marginTop: 2,
                  }}>
                    较昨日 {delta >= 0 ? '+' : ''}{format2(delta)}{row.suffix}
                    {deltaPct != null && ` (${delta >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%)`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    );
  }, [data, error]);

  // ── Responsive values ──
  const chartH = isMobile ? 200 : 300;
  const gridCols = isMobile ? '1fr' : '1fr 1fr';

  return (
    <div style={{ padding: isMobile ? 8 : 16 }}>
      {/* ── Top Bar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <Select
          style={{ width: isMobile ? '100%' : 200 }}
          placeholder="选择电站"
          value={station || undefined}
          options={stations.map((s) => ({ value: s, label: s }))}
          onChange={setStation}
        />
        <DatePicker
          style={{ width: isMobile ? '100%' : 180 }}
          value={date}
          onChange={(d) => setDate(d ?? dayjs())}
          allowClear={false}
        />
        {data?.mode && (
          <Tag color={data.mode === 'forecast' ? 'blue' : 'orange'}>
            {data.mode === 'forecast' ? '预测模式' : '历史回测模式'}
          </Tag>
        )}
      </div>

      {/* ── 2×2 Grid ── */}
      <Spin spinning={loading}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: 16,
          }}
        >
          {/* Chart 1: PV Forecast */}
          <ChartCard
            title="功率预测"
            option={pvChart ?? ({} as EChartsOption)}
            height={chartH}
          />

          {/* Chart 2: Curtailment + AGC */}
          <ChartCard
            title="限电率 + AGC"
            option={curtChart ?? ({} as EChartsOption)}
            height={chartH}
          />

          {/* Chart 3: EP + Price */}
          <ChartCard
            title="电价 + 策略"
            option={epChart ?? ({} as EChartsOption)}
            height={chartH}
          />

          {/* Quadrant 4: Expected Metrics */}
          {metricsCard}
        </div>
      </Spin>
    </div>
  );
}
